type TraceData = Record<string, unknown>;

interface NativeStartupMark {
  name: string;
  atIso: string;
  elapsedMs: number;
  data?: TraceData;
}

interface NativeStartupTrace {
  sessionId: string;
  platform: string;
  startedAt: number;
  startedAtIso: string;
  marks: NativeStartupMark[];
  completedAt?: number;
  totalElapsedMs?: number;
}

const ACTIVE_TRACE_KEY = 'native_startup_trace_active';
const TRACE_HISTORY_KEY = 'native_startup_traces';
const MAX_TRACE_HISTORY = 10;
const TRACE_MAX_AGE_MS = 5 * 60 * 1000;

const isNativePlatform = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const capacitor = (window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;

  return typeof capacitor?.isNativePlatform === 'function' && capacitor.isNativePlatform();
};

const getPlatform = () => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const capacitor = (window as Window & {
    Capacitor?: { getPlatform?: () => string };
  }).Capacitor;

  return typeof capacitor?.getPlatform === 'function' ? capacitor.getPlatform() : 'unknown';
};

function readJson<T>(key: string): T | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write failures in production startup flow.
  }
}

function removeKey(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage write failures in production startup flow.
  }
}

function exposeTraceHelpers() {
  if (typeof window === 'undefined') {
    return;
  }

  (window as Window & {
    __cleariestNativeStartup?: {
      getActiveTrace: () => NativeStartupTrace | null;
      getTraceHistory: () => NativeStartupTrace[];
      clearTraceHistory: () => void;
    };
  }).__cleariestNativeStartup = {
    getActiveTrace: () => readJson<NativeStartupTrace>(ACTIVE_TRACE_KEY),
    getTraceHistory: () => readJson<NativeStartupTrace[]>(TRACE_HISTORY_KEY) ?? [],
    clearTraceHistory: () => {
      removeKey(ACTIVE_TRACE_KEY);
      removeKey(TRACE_HISTORY_KEY);
    },
  };
}

function createTrace(): NativeStartupTrace {
  const startedAt = Date.now();

  return {
    sessionId: `startup-${startedAt}`,
    platform: getPlatform(),
    startedAt,
    startedAtIso: new Date(startedAt).toISOString(),
    marks: [],
  };
}

function getActiveTrace(): NativeStartupTrace | null {
  if (!isNativePlatform()) {
    return null;
  }

  const now = Date.now();
  const existing = readJson<NativeStartupTrace>(ACTIVE_TRACE_KEY);
  const isFresh = existing && !existing.completedAt && now - existing.startedAt < TRACE_MAX_AGE_MS;

  exposeTraceHelpers();
  return isFresh ? existing : null;
}

function ensureActiveTrace(): NativeStartupTrace | null {
  const existing = getActiveTrace();
  if (existing) {
    return existing;
  }

  if (!isNativePlatform()) {
    return null;
  }

  const trace = createTrace();

  writeJson(ACTIVE_TRACE_KEY, trace);
  exposeTraceHelpers();
  return trace;
}

export function beginNativeStartupTrace(markName: string, data?: TraceData) {
  const trace = ensureActiveTrace();
  if (!trace) {
    return;
  }

  const hasMark = trace.marks.some((mark) => mark.name === markName);
  if (!hasMark) {
    markNativeStartup(markName, data);
  }
}

export function markNativeStartup(name: string, data?: TraceData) {
  const trace = getActiveTrace();
  if (!trace) {
    return;
  }

  const now = Date.now();
  trace.marks.push({
    name,
    atIso: new Date(now).toISOString(),
    elapsedMs: now - trace.startedAt,
    ...(data ? { data } : {}),
  });

  writeJson(ACTIVE_TRACE_KEY, trace);
}

export function completeNativeStartupTrace(finalMarkName: string, data?: TraceData) {
  const trace = getActiveTrace();
  if (!trace) {
    return;
  }

  markNativeStartup(finalMarkName, data);

  const finalized = readJson<NativeStartupTrace>(ACTIVE_TRACE_KEY);
  if (!finalized) {
    return;
  }

  const completedAt = Date.now();
  finalized.completedAt = completedAt;
  finalized.totalElapsedMs = completedAt - finalized.startedAt;

  const history = readJson<NativeStartupTrace[]>(TRACE_HISTORY_KEY) ?? [];
  writeJson(TRACE_HISTORY_KEY, [finalized, ...history].slice(0, MAX_TRACE_HISTORY));
  removeKey(ACTIVE_TRACE_KEY);
  exposeTraceHelpers();
}
